<template>
  <q-table
    v-if="debugProperties.length !== 0"
    hide-bottom
    bordered
    flat
    dense
    row-key="id"
    :rows="rows"
    :columns="columns"
    :rows-per-page-options="[0]"
    :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
  >
    <template #header-cell="props">
      <q-th :props="props">
        {{ props.col.label }}
        <q-tooltip v-if="props.col.description" max-width="250px">
          <b>{{ props.col.label }}</b>
          <br />
          {{ props.col.description }}
        </q-tooltip>
      </q-th>
    </template>
    <template #body-cell-street_address_alias="{ value }">
      <q-td>
        <div v-for="alias in value" :key="alias">{{ alias }}</div>
      </q-td>
    </template>
    <template #body-cell-building_name_alias="{ value }">
      <q-td>
        <div v-for="alias in value" :key="alias">{{ alias }}</div>
      </q-td>
    </template>
    <template #body-cell-listing_urls="{ value }">
      <q-td>
        <div v-for="key in Object.keys(value).filter((key) => value[key].length)" :key="key">
          <a
            class="text-primary cursor-pointer"
            style="text-decoration: none"
            target="_blank"
            :href="value[key].length === 1 && !nonUrlSource(key) ? value[key][0] : undefined"
          >
            {{ key }} ({{ value[key].length }})
          </a>
          <q-menu
            v-if="value[key].length > 1 || nonUrlSource(key)"
            transition-duration="100"
            class="q-pa-sm"
          >
            <q-markdown-table flat>
              <q-tr v-for="url in value[key]" :key="url">
                <q-td>
                  <a
                    :href="!nonUrlSource(key) ? url : undefined"
                    target="_blank"
                    :class="{ 'text-primary': !nonUrlSource(key) }"
                  >
                    {{ url }}
                  </a>
                </q-td>
              </q-tr>
            </q-markdown-table>
          </q-menu>
        </div>
      </q-td>
    </template>
    <template #body-cell-helpful_links="{ row }">
      <q-td align="center">
        <q-btn
          :style="!row.building_website ? `visibility: hidden` : ''"
          color="primary"
          icon="link"
          flat
          clickable
          dense
          size="sm"
          :href="row.building_website"
          target="_blank"
        >
          <q-tooltip>Property Website</q-tooltip>
        </q-btn>
        <q-btn
          color="primary"
          icon="map"
          flat
          clickable
          dense
          size="sm"
          :href="`https://www.google.com/maps/dir/${row.street_address_alias.map((alias: string) => [alias, row.city, row.state, row.zip_code].join(' ')).join('/')}`"
          target="_blank"
        >
          <q-tooltip>Google Maps</q-tooltip>
        </q-btn>
      </q-td>
    </template>
  </q-table>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type {
  PropertyDetailsResponse,
  Availability,
  Price,
  AvailabilityPeriod,
  DebugProperty
} from 'common'
import { useAdminStore } from '@/stores/admin'

export default {
  name: 'PropertyCompareDebug',
  components: {},
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    }
  },
  data() {
    return {
      debugProperties: [] as DebugProperty[]
    }
  },
  computed: {
    columns() {
      return [
        {
          field: 'id',
          name: 'id',
          label: 'Property ID',
          align: 'left',
          sortable: true,
          description: 'The id of this property, helpful primarily for the dev team.'
        },

        {
          field: 'street_address_alias',
          name: 'street_address_alias',
          label: 'Street Addresses',
          align: 'left',
          description:
            'All of the street address aliases for this building. Seeing wrong aliases is a good sign that we must force split this property.'
        },
        {
          field: 'building_name_alias',
          name: 'building_name_alias',
          label: 'Building Names',
          align: 'left',
          description:
            'All of the building name aliases for this building. Seeing wrong aliases is a good sign that we must force split this property.'
        },
        {
          field: (row: PropertyDetailsResponse) => {
            const lookup: { [key: string]: string[] } = {
              'American Campus': [],
              'Apartment List': [],
              Apartments: [],
              Appfolio: [],
              Entrata: [],
              Knock: [],
              OnSite: [],
              RealPage: [],
              RentCafe: [],
              SightMap: [],
              StreetEasy: [],
              Yardi: [],
              Zillow: [],
              Zumper: []
            }
            const debugProperty = this.debugProperties.find((_) => _.id === row.id)
            if (debugProperty) {
              for (const url of debugProperty.listing_urls) {
                if (url.includes('americancampus.com')) {
                  lookup['American Campus'].push(url)
                } else if (url.includes('apartmentlist.com')) {
                  lookup['Apartment List'].push(url)
                } else if (url.includes('apartments.com')) {
                  lookup['Apartments'].push(url)
                } else if (url.includes('appfolio.com')) {
                  lookup['Appfolio'].push(url)
                } else if (url.includes('knock-')) {
                  lookup['Knock'].push(url.slice(6))
                } else if (url.startsWith('onsite-')) {
                  lookup['OnSite'].push(
                    `https://www.on-site.com/web/online_app/choose_unit?property_id=${url.slice(7)}`
                  )
                } else if (url.includes('rentcafe.com')) {
                  lookup['RentCafe'].push(url)
                } else if (url.includes('streeteasy.com')) {
                  lookup['StreetEasy'].push(url)
                } else if (url.startsWith('yardi-')) {
                  lookup['Yardi'].push(
                    `https://www.rentcafe.com/onlineleasing/apartmentsforrent/oleapplication.aspx?stepname=floorplan&myOlePropertyId=${url.slice(6)}`
                  )
                } else if (url.includes('zillow.com')) {
                  lookup['Zillow'].push(url)
                } else if (url.includes('zumper.com')) {
                  lookup['Zumper'].push(url)
                } else if (/^[0-9]+$/.test(url)) {
                  lookup['RealPage'].push(url)
                } else if (/^[a-zA-Z0-9]+$/.test(url)) {
                  lookup['SightMap'].push(`https://sightmap.com/embed/${url}`)
                } else {
                  lookup['Entrata'].push(url)
                }
              }
            }
            return lookup
          },
          name: 'listing_urls',
          label: 'Listing Urls',
          align: 'left',
          sortable: true,
          description: 'All of the listing urls that were fed into this record.'
        },
        {
          field: (row: PropertyDetailsResponse) => {
            const debugProperty = this.debugProperties.find((_) => _.id === row.id)
            return debugProperty?.latest_unit_source || ''
          },
          name: 'latest_unit_source',
          label: 'Unit Source',
          align: 'center',
          sortable: true,
          description: 'The source that is powering the latest unit availabilities.'
        },
        {
          field: '',
          name: 'helpful_links',
          label: 'Helpful Links',
          align: 'center',
          description: 'A few links to help debug the property.'
        }
      ]
    },
    rows() {
      return this.filteredProperties.map((_) => _.prop)
    }
  },
  created() {
    useAdminStore()
      .debugProperties(this.rows.map((_) => _.id))
      .then((properties) => {
        this.debugProperties = properties
      })
  },
  methods: {
    nonUrlSource(source: string) {
      return ['RealPage', 'Knock'].includes(source)
    }
  }
}
</script>
